import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useScrollTrigger,
  useTheme,
  Fade,
  Stack,
  Slide
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// HideOnScroll component to handle navbar hiding
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    // Trigger will be true when the user scrolls down, false when scrolling up
    threshold: 50
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = (props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const [scrolled, setScrolled] = useState(false);

  // Navigation items
  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle drawer toggle
  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  // Check if a path is active (including exact match and subpaths)
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Mobile drawer content
  const drawerContent = (
    <Box
      sx={{ 
        width: 280, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column' 
      }}
      role="presentation"
    >
      <Box sx={{ 
        py: 2, 
        px: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between' 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QrCodeIcon sx={{ color: 'primary.main', mr: 1.5, fontSize: 28 }} />
          <Typography variant="h6" component={RouterLink} to="/" sx={{ color: 'text.primary', textDecoration: 'none', fontWeight: 700 }}>
            QResto
          </Typography>
        </Box>
        <IconButton 
          onClick={toggleDrawer(false)}
          size="large"
          sx={{ 
            color: 'text.primary',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1, pt: 2 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={toggleDrawer(false)}
              sx={{
                py: 1.5,
                my: 0.5,
                mx: 1,
                borderRadius: 2,
                backgroundColor: isActive(item.path) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                }
              }}
            >
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: isActive(item.path) ? 600 : 500,
                  color: isActive(item.path) ? 'primary.main' : 'text.primary',
                  fontSize: '1rem'
                }}
              />
              {isActive(item.path) && (
                <Box 
                  sx={{ 
                    width: 4, 
                    height: 24, 
                    bgcolor: 'primary.main',
                    borderRadius: 4
                  }} 
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ p: 3 }}>
        <Button
          variant="contained"
          fullWidth
          component={RouterLink}
          to="/contact"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{ 
            borderRadius: 2,
            py: 1.2,
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 600
          }}
        >
          Get Started
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <HideOnScroll {...props}>
        <AppBar
          position="fixed"
          color="default"
          elevation={scrolled ? 2 : 0}
          sx={{
            backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            transition: 'all 0.3s ease',
            borderBottom: scrolled ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar 
              disableGutters 
              sx={{ 
                height: scrolled ? 70 : 80, 
                transition: 'height 0.3s ease'
              }}
            >
              {/* Logo */}
              <Fade in={true} timeout={1000}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mr: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'scale(1.05)' }
                }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      background: theme.palette.primary.main, 
                      p: '6px',
                      borderRadius: 1.5,
                      mr: 1.5
                    }}
                  >
                    <QrCodeIcon sx={{ 
                      color: 'white', 
                      fontSize: { xs: 24, md: 26 } 
                    }} />
                  </Box>
                  <Typography
                    variant="h5"
                    component={RouterLink}
                    to="/"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontSize: { xs: '1.2rem', md: '1.4rem' },
                      letterSpacing: '-0.5px',
                    }}
                  >
                    QResto
                  </Typography>
                </Box>
              </Fade>

              {/* Mobile menu button */}
              <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
                <IconButton
                  aria-label="open menu"
                  onClick={toggleDrawer(true)}
                  sx={{ 
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              {/* Desktop navigation */}
              <Stack 
                direction="row" 
                spacing={1.5} 
                sx={{ 
                  flexGrow: 1, 
                  display: { xs: 'none', md: 'flex' }, 
                  justifyContent: 'center',
                  ml: 4
                }}
              >
                {navItems.map((page) => (
                  <Button
                    key={page.label}
                    component={RouterLink}
                    to={page.path}
                    sx={{
                      color: isActive(page.path) ? 'primary.main' : 'text.secondary',
                      fontWeight: isActive(page.path) ? 600 : 500,
                      px: 2.5,
                      py: 1.2,
                      mx: 0.5,
                      borderRadius: 50,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      boxShadow: isActive(page.path) ? '0px 2px 8px rgba(25, 118, 210, 0.15)' : 'none',
                      background: isActive(page.path) 
                        ? 'linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.95))'
                        : 'transparent',
                      border: isActive(page.path) ? '1px solid rgba(25, 118, 210, 0.2)' : 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    {page.label}
                  </Button>
                ))}
              </Stack>

              {/* Get Started button */}
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button 
                  variant="contained" 
                  component={RouterLink}
                  to="/contact"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    px: 2.5, 
                    py: 1, 
                    borderRadius: 50,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    boxShadow: scrolled ? theme.shadows[1] : 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    }
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      {/* Drawer for mobile navigation */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          }
        }}
      >
        {drawerContent}
      </Drawer>
      
      {/* Toolbar spacer */}
      <Toolbar sx={{ height: { xs: 70, md: 80 }, mb: 1 }} />
    </>
  );
};

export default Navbar;